/* eslint-disable import/prefer-default-export */
import axios from 'axios';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import * as yup from 'yup';
import { CANADA_PHONE_REGEX, UK_PHONE_REGEX } from 'app/constants';

import config from '../../config.json';

const countryCodeByDeploymentRegion = {
  canada: 'CA',
  uk: 'GB',
};

const CURRENCY_BY_REGION = {
  canada: { symbol: '$', code: 'CAD', locale: 'en-CA' },
  uk: { symbol: '£', code: 'GBP', locale: 'en-GB' },
};

/**
 * Resolve the currency symbol/code/locale for the current deployment region.
 * Defaults to UK if the region is missing or unknown (preserves UK behaviour).
 */
export const getCurrency = (): { symbol: string; code: string; locale: string } =>
  CURRENCY_BY_REGION[config.deploymentRegion as keyof typeof CURRENCY_BY_REGION] ??
  CURRENCY_BY_REGION.uk;

export const axiosPrivate = axios.create({
  baseURL: config.apiUrl,
  headers: {
    'Content-Type': 'application/json',
    'client-id': config.clientId,
    'country-code':
      countryCodeByDeploymentRegion[
        (config.deploymentRegion as keyof typeof countryCodeByDeploymentRegion) || 'GB'
      ] || 'GB',
  },
});

// We need copy of above instance, because we should intercept
// only on calls that require token
export const axiosPublic = axios.create({
  baseURL: config.apiUrl,
  headers: {
    'Content-Type': 'application/json',
    'client-id': config.clientId,
    'country-code':
      countryCodeByDeploymentRegion[
        (config.deploymentRegion as keyof typeof countryCodeByDeploymentRegion) || 'GB'
      ] || 'GB',
  },
});

export const formatPriceWithCurrency = (
  number: number | string,
  currency = getCurrency().symbol
): string => {
  try {
    const parsedNumber = typeof number === 'string' ? parseFloat(number) : number;
    if (Number.isNaN(parsedNumber)) throw new Error(`Invalid argument: ${number}`);

    return `${currency}${parsedNumber.toFixed(2)}`;
  } catch (error) {
    console.error('Error formatting price', error);
    return `${currency}0.00`;
  }
};

export const formatPrice = (number: number | string): number => {
  try {
    const parsedNumber = typeof number === 'string' ? parseFloat(number) : number;
    if (Number.isNaN(parsedNumber)) throw new Error(`Invalid argument: ${number}`);

    return Number(parsedNumber.toFixed(2));
  } catch (error) {
    console.error('Error formatting price', error);
    return 0;
  }
};

export const downloadFile = (buffer: { data: ArrayBuffer }, fileName: string): void => {
  const blob = new Blob([new Uint8Array(buffer.data).buffer]);
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
};

export const capitalize = (str: string): string => {
  if (typeof str !== 'string') throw new Error('Unsupported argument');

  return str.slice(0, 1).toUpperCase() + str.slice(1).toLowerCase();
};

export const convertPatientAddressToObject = ({
  address,
  postcode,
}: {
  address: string;
  postcode: string;
}): {
  // eslint-disable-next-line camelcase
  house?: string;
  // eslint-disable-next-line camelcase
  addressLine: string;
  city?: string;
  postcode: string;
} => {
  const addressComponents = address.split(', ').filter(Boolean);
  const house = addressComponents.shift();
  const city = addressComponents.length > 1 ? addressComponents.pop() : undefined;
  const addressLine = addressComponents.join(', ');

  return {
    house,
    addressLine,
    city,
    postcode,
  };
};

export const getRelativeDateByTimestamp = (timestamp: number): string | null => {
  if (!timestamp) return null;
  const today = dayjs();
  const comingOrderDate = dayjs(timestamp * 1000);
  const formattedOrderDate = comingOrderDate.format('D MMM');

  if (comingOrderDate.isSame(today, 'day')) return `Today - ${formattedOrderDate}`;

  const dayDifference = comingOrderDate.diff(today, 'days');
  if (dayDifference === 1) return `Tomorrow - ${formattedOrderDate}`;
  if (dayDifference === -1) return `Yesterday - ${formattedOrderDate}`;

  const days = Math.abs(dayDifference) > 1 ? 'days' : 'day';
  const differenceRelation = dayDifference < 0 ? 'ago' : '';

  return `${Math.abs(dayDifference)} ${days} ${differenceRelation} - ${formattedOrderDate}`;
};

export const formatPluralCount = (amount: number, unit: string): string => {
  return `${amount} ${unit}${amount !== 1 ? 's' : ''}`;
};

export const downloadFileFromUrl = (url: string, fileName?: string): void => {
  if (typeof document === 'undefined') return;

  const downloadLink = document.createElement('a');
  downloadLink.href = url;
  downloadLink.download = fileName || '';
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
};

export const parseIntFromQuery = (queryParam: string): number | undefined => {
  const int = parseInt(queryParam, 10);
  if (Number.isNaN(int)) return undefined;

  return int;
};

/**
 * A utility to check if a field is required in a Yup schema
 * @param {Object} schema - The Yup schema object
 * @param {string} fieldName - The name of the field to check
 * @returns {boolean} - Whether the field is required
 */
export const isFormFieldRequired = (
  schema: yup.ObjectSchema<Record<string, unknown>>,
  fieldName: string
): boolean => {
  // Get the field schema from the object schema
  const fieldSchema = schema.fields?.[fieldName];

  if (!fieldSchema) return false;

  // Check if the field has any required tests
  if ('tests' in fieldSchema && Array.isArray(fieldSchema.tests)) {
    return fieldSchema.tests.some((test) => test.OPTIONS.name === 'required');
  }

  return false;
};

/**
 * Get the phone number regex for the current deployment region
 * @returns {RegExp} - The phone number regex
 */
export const getPhoneNumberRegex = (): RegExp => {
  if (config.deploymentRegion === 'canada') return CANADA_PHONE_REGEX;
  return UK_PHONE_REGEX;
};

const ISO_8601_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,9})?(Z|[+-]\d{2}:?\d{2})$/;
export const isValidTimezone = (timezone: string): boolean => {
  if (typeof timezone !== 'string' || !timezone) return false;
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
};

/**
 * Parse a UTC ISO 8601 timestamp and convert its display to the given IANA timezone.
 *
 * Expects `date` to be a proper UTC ISO 8601 string (e.g. `2026-04-02T15:00:00.000Z`) — the
 * embedded `Z`/offset is honoured to pin the absolute moment, then the display is converted
 * to `timezone`. If `timezone` is omitted, returns a Dayjs in the browser's local zone.
 *
 * On invalid input (non-ISO `date` or unknown IANA `timezone`), logs to `console.error` and
 * returns an invalid Dayjs (`dayjs(NaN)`). Callers can chain `.format(...)` safely — the result
 * will render as the literal string `"Invalid Date"` rather than throwing.
 *
 * @param date - UTC ISO 8601 timestamp
 * @param timezone - Optional IANA timezone name (e.g. `Europe/London`, `America/Toronto`)
 * @returns A Dayjs in the requested timezone, or an invalid Dayjs on bad input
 */
export const convertISODateToDayJsWithTimezone = (date: string, timezone?: string): Dayjs => {
  if (typeof date !== 'string' || !ISO_8601_REGEX.test(date)) {
    console.error(`convertISODateToDayJsWithTimezone: expected ISO 8601 string, got ${date}`);
    return dayjs(NaN);
  }

  if (!timezone) return dayjs(date);

  if (!isValidTimezone(timezone)) {
    console.error(`convertISODateToDayJsWithTimezone: invalid IANA timezone "${timezone}"`);
    return dayjs(NaN);
  }

  return dayjs(date).tz(timezone);
};

/**
 * Check if a string is a valid UUID
 * @param {string} uuid - The string to check
 * @returns {boolean} - Whether the string is a valid UUID
 */
export const isValidUUID = (uuid: string): boolean => {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(uuid);
};

/**
 * Safely parse a JSON string
 * @param {string} json - The JSON string to parse
 * @returns {Object|null} - The parsed object or null if the string is not valid JSON
 */
export const safeJSONParse = (json: string): unknown | null => {
  try {
    return JSON.parse(json);
  } catch (error) {
    console.error('Error parsing JSON', error);
    return null;
  }
};

export const formatCurrency = (
  number: string | number,
  currency = getCurrency().code,
  locale = getCurrency().locale
): string | null => {
  if (!Number.isFinite(Number(number))) return null;

  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(Number(number));
};

/**
 * Format a phone number by removing all spaces
 * @param {string} phoneNumber - The phone number to format
 * @returns {string|null} - The formatted phone number or null if the phone number is not a string or is empty
 */
export const formatPhoneNumber = (phoneNumber: string): string | null => {
  if (typeof phoneNumber !== 'string') return null;

  const trimmedPhoneNumber = phoneNumber.replace(/\s/g, '');
  if (trimmedPhoneNumber.length === 0) return null;

  return trimmedPhoneNumber;
};

/**
 * Formats a date string to locale format with date and time
 * @param {string} dateString - ISO date string or date string
 * @param {string} [locale='en-GB'] - Locale string (defaults to 'en-GB')
 * @returns {string} Formatted date string in DD/MM/YYYY, HH:MM format (or locale equivalent)
 */
export const formatDateTime = (dateString: string, locale = 'en-GB'): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export function formatDate(value: string | null): string {
  if (!value) return '-';
  const date = new Date(value);
  if (isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-GB');
}

export const isDevelopmentEnvironment = (): boolean => {
  return config.env === 'local' || config.env === 'development';
};

export const getFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes}bytes`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)}KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(2)}MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)}GB`;
};

export const resolveContentType = (file: File): string => {
  if (file.type) return file.type;

  // Fallback to extension-based content type mapping
  const fileName = file.name.toLowerCase();
  const extension = fileName.substring(fileName.lastIndexOf('.'));

  const extensionToContentType: Record<string, string> = {
    '.csv': 'text/csv',
    '.txt': 'text/plain',
    '.json': 'application/json',
    '.pdf': 'application/pdf',
    '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    '.xls': 'application/vnd.ms-excel',
    '.xml': 'application/xml',
    '.zip': 'application/zip',
  };

  return extensionToContentType[extension] || 'application/octet-stream';
};
